import React, { useState } from 'react';
import { Table, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { truncateMiddleString } from '../../../../utils/stringUtils';

interface AttachmentExpandableTableProps {
  summaryRows: any[];
  attachmentsResult: any[];
  onFetchAttachments: (query: string, id: number) => void;
  onViewPdf?: (row: any) => void;
  onDownloadAction?: (row: any) => void;
  sortBySender: boolean;
  RECORD_LIST_ATTACHMENT_BY_SENDER_HEADERS: any[];
  RECORD_LIST_ATTACHMENT_HEADERS: any[];
  searchQuery: string;
}

const AttachmentExpandableTable: React.FC<AttachmentExpandableTableProps> = ({
  summaryRows,
  attachmentsResult,
  onFetchAttachments,
  onViewPdf,
  onDownloadAction,
  sortBySender,
  RECORD_LIST_ATTACHMENT_BY_SENDER_HEADERS,
  RECORD_LIST_ATTACHMENT_HEADERS,
  searchQuery,
}) => {
  const [expandedKey, setExpandedKey] = useState<number | null>(null);

  React.useEffect(() => {
    if (summaryRows.length > 0 && expandedKey === null) {
      setExpandedKey(summaryRows[0].subjectQMEScheduleID);
    }
  }, [summaryRows]);

  const handleToggle = (id: number) => {
    if (expandedKey === id) {
      setExpandedKey(null);
    } else {
      setExpandedKey(id);
      onFetchAttachments(searchQuery, id);
    }
  };

  const headers = sortBySender
    ? RECORD_LIST_ATTACHMENT_BY_SENDER_HEADERS
    : RECORD_LIST_ATTACHMENT_HEADERS;


  const renderCellContent = (header: any, row: any) => {
    const value = row[header.key];

    if (header.key === "documentTypeName") {
      const tooltipContent = sortBySender ? row.documentCategory : row.senderKeyName;

      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span className="text-truncate" style={{ maxWidth: "200px" }} title={value}>
            {value}
          </span>
          <OverlayTrigger
            placement="auto"
            overlay={<Tooltip id={`tooltip-info-${row.id}`}>{tooltipContent}</Tooltip>}
          >
            <span
              className="material-symbols-outlined"
              style={{
                cursor: "pointer",
                color: "#f39c12",
                fontSize: "18px",
                marginLeft: "8px",
                verticalAlign: "middle",
              }}
            >
              info
            </span>
          </OverlayTrigger>
        </div>
      );
    }

    if (header.key === "fileName") {
      return (
        <OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-file-${row.id}`}>{value}</Tooltip>}>
          <span className="text-truncate d-inline-block" style={{ maxWidth: "250px" }}>
            {value && value.length > 30 ? truncateMiddleString(value) : value}
          </span>
        </OverlayTrigger>
      );
    }

    if (header.key === 'Attachmentactions') {
      const fileUrl = row.fileName;
      const isPdf = fileUrl?.toLowerCase().endsWith('.pdf');
      const isImage =
        /\.(png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
          fileUrl || '',
        );

      return (
        <td key={header.key} style={{ padding: '8px' }}>
          {fileUrl && (
            <>
              {(isPdf || isImage) && (
                <span
                  className='material-icons material-symbols-outlined color_brand mx-1'
                  onClick={() => onViewPdf?.(row)} // Unified viewer handler
                  style={{ cursor: 'pointer' }}
                  title='View File'
                >
                  document_search
                </span>
              )}
              <span
                className='material-icons color_brand mx-1'
                onClick={() =>
                  onDownloadAction?.(row)
                }
                style={{ cursor: 'pointer' }}
              >
                download
              </span>
            </>
          )}
        </td>
      );
    }

    return (
      <OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-cell-${header.key}-${row.id}`}>{value}</Tooltip>}>
        <span className="text-truncate d-inline-block" style={{ maxWidth: "100%" }}>
          {value}
        </span>
      </OverlayTrigger>
    );
  };

  return (
    <div className="table-responsive border-0 mb-2">
      <Table style={{ tableLayout: "fixed" }}>
        <thead>
          <tr>
            <th style={{ width: "50px", backgroundColor: "#e0f7fa" }}></th>
            {headers.map((header) => (
              <th
                key={header.key}
                style={{
                  border: "none",
                  padding: "8px",
                  backgroundColor: "#e0f7fa",
                  color: "#000",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  textAlign: header.key === "download" ? "center" : "left",
                  ...header.style,
                }}
              >
                {header.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {summaryRows.length > 0 ? (
            summaryRows.map((summary) => (
              <React.Fragment key={summary.subjectQMEScheduleID}>
                <tr style={{ backgroundColor: "#f2f2f2" }}>
                  <td className="text-center">
                    <span
                      className="material-symbols-outlined"
                      style={{ cursor: "pointer", color: "#0098d7" }}
                      onClick={() => handleToggle(summary.subjectQMEScheduleID)}
                    >
                      {expandedKey === summary.subjectQMEScheduleID
                        ? "do_not_disturb_on"
                        : "add_circle"}
                    </span>
                  </td>
                  <td colSpan={headers.length}>
                    <div className="d-flex align-items-center">
                      {new Date(summary.dateOfAppointment).toLocaleDateString("en-US")} |{" "}
                      {summary.caseTypeExamType}
                      <Badge bg="primary" className="ms-2 primary-badge">
                        {summary.countOfAttachment}
                      </Badge>
                    </div>
                  </td>
                </tr>
                {expandedKey === summary.subjectQMEScheduleID && (
                  <>
                    {attachmentsResult.length > 0 ? (
                      attachmentsResult.map((row, idx) => (
                        <tr key={row.id || idx}>
                          <td></td>
                          {headers.map((header) => (
                            <td key={header.key} style={{ padding: "8px" }}>
                              {renderCellContent(header, row)}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td></td>
                        <td colSpan={headers.length} className="text-center py-3">
                          <span className="no_rec_data">No Record Found</span>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={headers.length + 1} className="text-center">
                <span className="no_rec_data">No Record Found</span>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default AttachmentExpandableTable;
